import React, { useMemo } from 'react';
import { useTimelineEventDetailData } from '../../data/DataProviders/TimelineEventDetailDataProvider';
import { makeListGroupComponent } from '../ListGroups/ListGroup';
import { isDataLoaded } from '../../types/data-providers';
import {
    TimelineEventDetailListItem,
    TimelineEventDetailListItemValue,
} from '../TimelineEventDetailListItem/TimelineEventDetailListItem';
import { useDeviceData } from '../../data/DataProviders/DeviceDataProvider';
import { DataIndicators } from '../DataIndicators';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import {
    Button,
    CircularProgress,
    Typography,
    Link,
    Box,
    Card,
    List,
    ListSubheader,
    Divider,
    TextField,
    Grid,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Icon } from '../../lib/icons';
import * as TimelineEventDetailCardHelpers from './helpers';
import { getUpdatedChannelListForTimeline } from '../../lib/signalr-helper';
import { useDispatch, useSelector } from 'react-redux';
import { Thunks, Actions } from '../../actions';
import { useAuthHelper } from '../AuthContextProvider';
import { Selectors } from '../../selectors';
import * as PXBColors from '@brightlayer-ui/colors';
import { CONTACT_EMAIL, CONTACT_PHONENO } from '../../constants';
import CheckIcon from '@mui/icons-material/Check';
import { AlarmStatus } from '../../lib/alarm-helpers';
import _ from 'lodash';
import { DeviceChannelData, DeviceVendor } from '../../types/device';
import { useSignalRSiteData } from '../../data/DataProviders/SignalRSiteDataProvider';
import { getPrecisionValue } from '../../lib/precision-helper';
import { isRecordFound, useSelectedOrganizationData } from '../../hooks/data-hooks';
import { AlarmChannelDetailDataProvider } from '../../data/DataProviders/AlarmChannelDetailDataProvider';
import { AlarmChannelDetails } from './AlarmChannelDetail';
import { ConfirmationPopUp } from '../ConfirmationPopUp/ConfirmationPopUp';
import { DatetimeListDataProvider } from '../../data/DataProviders/DatetimeListDataProvider';
const useStyles = makeStyles(() => ({
    alarmHeaderContent: {
        backgroundColor: PXBColors.white[50],
        borderBottom: '1px solid gray',
        boxShadow: '0px 0px 6px 0px',
        marginBottom: '15px',
        padding: '15px 0px',
        marginTop: '16px',
    },
    alrmPadding: {
        // padding:'15px 0px'
    },
}));

const TimelineEventDetailList = makeListGroupComponent<TimelineEventDetailListItemValue>();

export const TimelineEventDetailCard: React.FC = () => {
    const classes = useStyles();
    const timelineEventDetailData = useTimelineEventDetailData();
    const selectedOrganization = useSelectedOrganizationData();
    const deviceDetailData = useDeviceData();
    const { t } = useLanguageLocale();
    const siteRealTimeData = useSignalRSiteData();
    const Dispatch = useDispatch();
    const authHelper = useAuthHelper();
    const languageLocale = useLanguageLocale();
    const alarmAcknowledgeStarted = useSelector(Selectors.Alarm.alarmAcknowledgeStarted);
    const alarmAcknowledgeFailed = useSelector(Selectors.Alarm.alarmAcknowledgeFailed);
    const alarmAcknowledgeInfo = useSelector(Selectors.Alarm.alarmAcknowledgeInfo);
    const [channelData, setChannelData] = React.useState<DeviceChannelData[]>([]);
    const [statusData, setStatusData] = React.useState<{
        latestTime: string | null;
        status: string;
    } | null>(null);
    const organizationName = isRecordFound(selectedOrganization) ? selectedOrganization.value.name : '';
    const [open, setOpen] = React.useState(true);
    const [openAlarmAck, setOpenAlarmAck] = React.useState(false);
    const [note, setNote] = React.useState<string>('');
    const [isStarFishDevice, setIsStarFishDevice] = React.useState(false);

    React.useEffect((): void => {
        Dispatch(Actions.Alarm.alarmAcknowledgementUnmounted());
    }, [Dispatch]);

    React.useEffect(() => {
        if (isDataLoaded(deviceDetailData) && isDataLoaded(deviceDetailData.value.channelData)) {
            setChannelData(deviceDetailData.value.channelData.value);
        }

        if (alarmAcknowledgeInfo.statusCode === 200 && isDataLoaded(timelineEventDetailData)) {
            timelineEventDetailData.value.status = alarmAcknowledgeInfo.status;
            timelineEventDetailData.value.latestTime = alarmAcknowledgeInfo.acknowledgeTime;
        }

        if (isDataLoaded(timelineEventDetailData)) {
            setStatusData({
                latestTime: timelineEventDetailData.value.latestTime,
                status: timelineEventDetailData.value.status,
            });
        }
    }, [deviceDetailData, timelineEventDetailData, alarmAcknowledgeInfo]);

    React.useEffect(() => {
        if (siteRealTimeData && isDataLoaded(deviceDetailData)) {
            if (isDataLoaded(timelineEventDetailData) && isDataLoaded(deviceDetailData.value.channelData)) {
                const updatedAlarm = _.find(siteRealTimeData && siteRealTimeData.alarmStatus, {
                    alarmId: timelineEventDetailData.value.alarmId,
                });
                if (updatedAlarm) {
                    timelineEventDetailData.value.status = updatedAlarm.status;
                    timelineEventDetailData.value.latestTime = updatedAlarm.dateTime;
                    const alarmDeviceChannel = deviceDetailData.value.channelData.value.find(
                        (channel: DeviceChannelData) => channel.id === updatedAlarm.channelId
                    );
                    if (alarmDeviceChannel) alarmDeviceChannel.value = getPrecisionValue(updatedAlarm.value);
                    setChannelData(deviceDetailData.value.channelData.value);
                    setStatusData({
                        latestTime: updatedAlarm.dateTime,
                        status: updatedAlarm.status,
                    });
                }
            }
            if (
                siteRealTimeData &&
                siteRealTimeData.channelData &&
                siteRealTimeData.channelData.length > 0 &&
                isDataLoaded(deviceDetailData.value.channelData) &&
                isDataLoaded(deviceDetailData.value.deviceProperties)
            ) {
                const clone = getUpdatedChannelListForTimeline(
                    isDataLoaded(timelineEventDetailData) ? timelineEventDetailData.value.deviceId : '',
                    siteRealTimeData.channelData,
                    deviceDetailData.value.channelData.value
                );
                setChannelData(clone);
            }
        }
    }, [siteRealTimeData]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect((): void => {
        if (isDataLoaded(timelineEventDetailData)) {
            setOpenAlarmAck(false);
        }
    }, [alarmAcknowledgeInfo]); // eslint-disable-line react-hooks/exhaustive-deps

    const alarmAcknowledgement = React.useCallback(
        (incidentId: number): void => {
            setOpen(true);
            setNote('');
            if (
                isDataLoaded(timelineEventDetailData) &&
                timelineEventDetailData.value.deviceVendor.toLowerCase() === DeviceVendor.SierraWireless
            ) {
                setOpenAlarmAck(true);
            } else if (
                isDataLoaded(timelineEventDetailData) &&
                (timelineEventDetailData.value.deviceVendor === DeviceVendor.SFStarfishGateway ||
                    timelineEventDetailData.value.deviceVendor?.toLowerCase() === DeviceVendor.Eaton)
            ) {
                setIsStarFishDevice(true);
                setOpenAlarmAck(true);
            } else Dispatch(Thunks.Alarm.AlarmAcknowledgement(incidentId, organizationName, authHelper));
        },
        [Dispatch, authHelper, organizationName, timelineEventDetailData]
    );
    const datenow = new Date();
    const errorDate = languageLocale.formatDateTime(datenow.toString());

    const deviceId = isDataLoaded(timelineEventDetailData) ? timelineEventDetailData.value.deviceId : '';
    const alarmId = isDataLoaded(timelineEventDetailData) ? timelineEventDetailData.value.alarmId : '';

    const closePopUp = (): void => {
        setOpen(false);
    };

    const onSierraAlarmAck = React.useCallback((): void => {
        if (isDataLoaded(timelineEventDetailData)) {
            Dispatch(
                Thunks.Alarm.SierraAlarmAcknowledgement(
                    authHelper,
                    timelineEventDetailData.value.alarmId,
                    timelineEventDetailData.value.deviceId,
                    note
                )
            );
        }
    }, [timelineEventDetailData, note]); // eslint-disable-line react-hooks/exhaustive-deps

    const onStarFishAlarmAck = React.useCallback((): void => {
        if (isDataLoaded(timelineEventDetailData)) {
            Dispatch(
                Thunks.Alarm.StarfishAlarmAcknowledgement(
                    authHelper,
                    timelineEventDetailData.value.alarmId,
                    timelineEventDetailData.value.deviceId,
                    note
                )
            );
        }
    }, [timelineEventDetailData, note]); // eslint-disable-line react-hooks/exhaustive-deps

    const onCancelAlarmAck = (): void => {
        setNote('');
        setOpen(false);
        setOpenAlarmAck(false);
    };

    const isDisable = (): boolean => Boolean(note !== null && note.trimEnd() === '');

    const TimelineEventDetail = useMemo(() => {
        const items = isDataLoaded(timelineEventDetailData)
            ? [
                  // @ts-ignore
                  ...TimelineEventDetailCardHelpers.getDeviceName(deviceDetailData, t),
                  ...TimelineEventDetailCardHelpers.getAlarmStatus(timelineEventDetailData.value),
                  ...TimelineEventDetailCardHelpers.getLastDataReceived(statusData),
                  ...TimelineEventDetailCardHelpers.getTimeAlarmSent(timelineEventDetailData.value),
                  ...TimelineEventDetailCardHelpers.getLastReading(timelineEventDetailData.value),
                  ...TimelineEventDetailCardHelpers.getChannelData(channelData),
                  ...TimelineEventDetailCardHelpers.getDeviceDetails(deviceDetailData, t),
              ]
            : [];
        return (
            <TimelineEventDetailList
                group={{
                    items,
                }}
                component={TimelineEventDetailListItem}
                componentTestId="timeline-event-detail-card"
            />
        );
    }, [timelineEventDetailData, statusData, channelData, deviceDetailData, t]);

    return (
        <div>
            <DataIndicators
                emptyIcon="Notifications"
                emptyMessage={t('DASHBOARD.NO_DATA')}
                errorMessage={t('DASHBOARD.ERROR_LOADING_DATA')}
                data={timelineEventDetailData}
                isEmpty={false}
            />
            {isDataLoaded(deviceDetailData) && isDataLoaded(timelineEventDetailData) && (
                <>
                    <Grid container component="div" spacing={2}>
                        <Grid
                            item
                            xs={12}
                            className={`${
                                isDataLoaded(deviceDetailData.value.deviceProperties) &&
                                deviceDetailData.value.deviceProperties.value.type?.toLowerCase() ===
                                    DeviceVendor.Insulgard
                                    ? classes.alarmHeaderContent
                                    : classes.alrmPadding
                            }`}
                        >
                            <Box display="flex" justifyContent="right" alignItems="end" flexDirection="row">
                                <>
                                    {timelineEventDetailData.value.deviceVendor.toLowerCase() !==
                                        DeviceVendor.EatonCorporation && (
                                        <Button
                                            data-testid="acknowledge-button"
                                            style={{
                                                marginRight: 15,
                                                height: '40px',
                                                backgroundColor:
                                                    timelineEventDetailData.value.deviceVendor ===
                                                        DeviceVendor.Wellaware &&
                                                    alarmAcknowledgeStarted &&
                                                    PXBColors.blue[200],
                                                color:
                                                    timelineEventDetailData.value.deviceVendor ===
                                                        DeviceVendor.Wellaware && alarmAcknowledgeStarted
                                                        ? 'white'
                                                        : '    ',
                                            }}
                                            onClick={(): void => {
                                                alarmAcknowledgement(timelineEventDetailData.value.incidentId);
                                            }}
                                            variant="contained"
                                            color="primary"
                                            startIcon={
                                                timelineEventDetailData.value.status === AlarmStatus.Closed ||
                                                timelineEventDetailData.value.status === AlarmStatus.Acknowledged ? (
                                                    <CheckIcon />
                                                ) : timelineEventDetailData.value.deviceVendor ===
                                                      DeviceVendor.Wellaware && alarmAcknowledgeStarted ? (
                                                    <CircularProgress
                                                        size="1rem"
                                                        style={{
                                                            color: 'white',
                                                        }}
                                                    />
                                                ) : (
                                                    <Icon.NotificationsOff />
                                                )
                                            }
                                            disabled={
                                                timelineEventDetailData.value.status === AlarmStatus.Closed ||
                                                timelineEventDetailData.value.status === AlarmStatus.Acknowledged ||
                                                alarmAcknowledgeStarted
                                            }
                                        >
                                            {timelineEventDetailData.value.status === AlarmStatus.Closed ||
                                            timelineEventDetailData.value.status === AlarmStatus.Acknowledged
                                                ? t('EVENT_DETAILS.ACKNOWLEDGED')
                                                : t('EVENT_DETAILS.ACKNOWLEDGE')}
                                        </Button>
                                    )}
                                </>
                            </Box>
                        </Grid>
                    </Grid>
                    <Grid container>
                        <Grid
                            item
                            style={{ paddingRight: '15px', marginLeft: '5px' }}
                            xs={
                                isDataLoaded(deviceDetailData.value.deviceProperties) &&
                                deviceDetailData.value.deviceProperties.value.type?.toLowerCase() ===
                                    DeviceVendor.Insulgard
                                    ? 5
                                    : 12
                            }
                        >
                            <Box>
                                {(timelineEventDetailData.value.status === AlarmStatus.Closed ||
                                    timelineEventDetailData.value.status === AlarmStatus.Acknowledged) && (
                                    <Card
                                        data-testid="alarm-ack-card"
                                        style={{
                                            marginTop:
                                                isDataLoaded(deviceDetailData.value.deviceProperties) &&
                                                deviceDetailData.value.deviceProperties.value.type?.toLowerCase() ===
                                                    DeviceVendor.Insulgard
                                                    ? '0px'
                                                    : '20px',
                                        }}
                                    >
                                        <List
                                            subheader={
                                                <ListSubheader
                                                    color="primary"
                                                    component="div"
                                                    style={{
                                                        marginTop: '18px',
                                                        fontWeight: 600,
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            flex: '1',
                                                        }}
                                                    >
                                                        Acknowledge Details
                                                    </div>
                                                </ListSubheader>
                                            }
                                        >
                                            <Divider />
                                            <Typography
                                                data-testid="notes"
                                                variant="subtitle2"
                                                style={{
                                                    margin: '10px 15px',
                                                }}
                                            >
                                                {note || timelineEventDetailData.value.note}
                                            </Typography>
                                            <Typography
                                                data-testid="notes-user"
                                                variant="subtitle2"
                                                style={{
                                                    margin: '10px 15px',
                                                    color: '#9CA5A9',
                                                }}
                                            >
                                                {t('EVENT_DETAILS.ACKNOWLEDGE_BY', {
                                                    replace: {
                                                        user:
                                                            alarmAcknowledgeInfo.acknowledgeUser ||
                                                            timelineEventDetailData.value.acknowledgeUser,
                                                    },
                                                })}
                                                {languageLocale.formatDate(alarmAcknowledgeInfo.acknowledgeTime) ||
                                                    languageLocale.formatDate(
                                                        timelineEventDetailData.value.acknowledgeTime
                                                    )}{' '}
                                                at{' '}
                                                {languageLocale.formatTime(alarmAcknowledgeInfo.acknowledgeTime) ||
                                                    languageLocale.formatTime(
                                                        timelineEventDetailData.value.acknowledgeTime
                                                    )}
                                            </Typography>
                                        </List>
                                    </Card>
                                )}

                                {alarmAcknowledgeFailed &&
                                    (timelineEventDetailData.value.status === AlarmStatus.Open ||
                                        timelineEventDetailData.value.status === AlarmStatus.Cleared) && (
                                        <>
                                            <ConfirmationPopUp
                                                title={t('EVENT_DETAILS.PLEASE_RETRY')}
                                                body={
                                                    <Typography
                                                        component="p"
                                                        variant="subtitle2"
                                                        style={{
                                                            color: 'gray',
                                                        }}
                                                    >
                                                        {t('EVENT_DETAILS.ACKNOWLWEGED_ERROR', {
                                                            replace: {
                                                                errorDate: errorDate,
                                                                phoneno: CONTACT_PHONENO,
                                                            },
                                                        })}
                                                        <Link underline="always" href={`mailto:${CONTACT_EMAIL}`}>
                                                            {CONTACT_EMAIL}
                                                        </Link>
                                                    </Typography>
                                                }
                                                okButtonText={'OK'}
                                                handleOk={closePopUp}
                                                show={alarmAcknowledgeFailed && open}
                                                width="xs"
                                                okButtonShow={true}
                                            ></ConfirmationPopUp>
                                        </>
                                    )}
                            </Box>

                            <div style={{ marginTop: '18px' }}>
                                <Card>
                                    <List
                                        style={{ paddingBottom: '0px' }}
                                        subheader={
                                            <ListSubheader
                                                color="primary"
                                                component="div"
                                                style={{
                                                    fontWeight: 600,
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    paddingBottom: '0px',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        flex: '1',
                                                    }}
                                                >
                                                    Alarm Details
                                                </div>
                                            </ListSubheader>
                                        }
                                    >
                                        {TimelineEventDetail}
                                    </List>
                                </Card>
                            </div>
                        </Grid>

                        <Grid xs={7} item style={{ marginLeft: '-7px' }}>
                            {isDataLoaded(deviceDetailData.value.deviceProperties) &&
                                deviceDetailData.value.deviceProperties.value.type?.toLowerCase() ===
                                    DeviceVendor.Insulgard && (
                                    <AlarmChannelDetailDataProvider deviceId={deviceId} alarmId={alarmId}>
                                        <DatetimeListDataProvider deviceId={deviceId}>
                                            <AlarmChannelDetails />
                                        </DatetimeListDataProvider>
                                    </AlarmChannelDetailDataProvider>
                                )}
                        </Grid>
                    </Grid>
                </>
            )}

            {openAlarmAck && (
                <ConfirmationPopUp
                    title={t('EVENT_DETAILS.ACKNOWLEDGE_ALARM')}
                    body={
                        <>
                            <Box>
                                <Typography component="p" variant="subtitle2">
                                    {t('EVENT_DETAILS.ACKNOWLEDGE_NOTE_1')}
                                </Typography>
                                <Typography
                                    component="p"
                                    variant="subtitle2"
                                    style={{
                                        marginTop: '20px',
                                        marginBottom: '40px',
                                    }}
                                >
                                    {t('EVENT_DETAILS.ACKNOWLEDGE_NOTE_2')}
                                </Typography>
                                <Divider
                                    style={{
                                        marginLeft: '-24px',
                                        marginRight: '-24px',
                                    }}
                                />
                                <TextField
                                    inputProps={{
                                        'data-testid': 'alarm-ack-note',
                                        maxLength: 255,
                                    }}
                                    style={{
                                        width: '100%',
                                        marginTop: '32px',
                                        marginBottom: '40px',
                                    }}
                                    id="Notes"
                                    multiline
                                    maxRows={Infinity}
                                    label="Notes"
                                    variant="filled"
                                    value={note}
                                    onChange={(e): void => setNote(e.target.value)}
                                    helperText={
                                        <Box component="span" display="flex" justifyContent="flex-end">
                                            {note.length}/255
                                        </Box>
                                    }
                                />
                            </Box>
                        </>
                    }
                    okButtonText={t('EVENT_DETAILS.ACKNOWLEDGE')}
                    handleOk={isStarFishDevice ? onStarFishAlarmAck : onSierraAlarmAck}
                    closeDialog={onCancelAlarmAck}
                    disableOk={isDisable()}
                    loading={alarmAcknowledgeStarted}
                    show={openAlarmAck}
                    width="xs"
                    okButtonShow={true}
                    cancelButtonsShow={true}
                    cancelButtonText={t('ACTIONS.CANCEL')}
                ></ConfirmationPopUp>
            )}
        </div>
    );
};
